/**
 * Sidebar v2 row actions: pin/snooze/settle/rename/archive/delete callbacks
 * and the row context menu, over forge's client-side overlay and server
 * settle path.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (attempt wrappers and context menu, ~lines 3060-3320 and 4006-4290) at
 * pin e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: pin, order keys, and snooze write to
 * forge's uiState overlay; settling clears the overlay's pin and snooze the
 * way upstream's server does. The context menu is a v2-local item list
 * rather than forge's shared action-menu builder, which carries runtime
 * controls the v2 shell does not render.
 */
import { useCallback, useState } from "react";
import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";

import { toastManager } from "~/components/ui/toast";
import { commitThreadRename } from "~/hooks/commitThreadRename";
import { readLocalApi } from "~/localApi";
import { isMacPlatform } from "~/lib/utils";
import { useUiStateStore } from "~/uiStateStore";
import { useThreadSelectionStore } from "~/threadSelectionStore";
import { pinOrderKeyBetween } from "./sidebarV2Sort";
import { resolveSnoozePresets } from "./sidebarV2Snooze";
import {
  isSidebarNestedLinkClick,
  isTrailingDoubleClick,
  type SidebarSection,
} from "./sidebarV2List";
import type { TimestampFormat } from "@cmmd-forge/contracts";

/** Show the v2 row context menu and dispatch the picked action. The menu
 * is v2-local (lifecycle, snooze, pin, rename, destructive tail) rather
 * than forge's shared action-menu builder, which carries runtime controls
 * the v2 shell does not render. */
async function dispatchRowContextMenu(context: {
  readonly threadRef: ScopedThreadRef;
  readonly threadTitle: string;
  readonly position: { x: number; y: number };
  readonly timestampFormat: TimestampFormat;
  readonly onSettle: (threadRef: ScopedThreadRef) => void;
  readonly onUnsettle: (threadRef: ScopedThreadRef) => void;
  readonly onPin: (threadRef: ScopedThreadRef) => void;
  readonly onSnooze: (
    threadRef: ScopedThreadRef,
    preset: { readonly snoozedUntil: string },
  ) => void;
  readonly onStartRename: (threadRef: ScopedThreadRef, title: string) => void;
  readonly onArchive: (threadRef: ScopedThreadRef) => void;
  readonly onDelete: (threadRef: ScopedThreadRef) => void;
  readonly onMarkUnread: (threadId: string) => void;
}): Promise<void> {
  const api = readLocalApi();
  if (!api) return;
  const presets = resolveSnoozePresets(new Date(), context.timestampFormat).map((preset) => ({
    id: preset.snoozedUntil,
    label: `${preset.label} (${preset.whenLabel})`,
  }));
  const items: readonly { id: string; label: string; destructive?: true }[] = [
    { id: "settle", label: "Settle thread" },
    ...presets.map((preset) => ({ id: `snooze:${preset.id}`, label: `Snooze: ${preset.label}` })),
    { id: "pin", label: "Pin thread" },
    { id: "rename", label: "Rename thread" },
    { id: "mark-unread", label: "Mark unread" },
    { id: "archive", label: "Archive thread" },
    { id: "delete", label: "Delete thread", destructive: true },
  ];
  const clicked = await api.contextMenu.show(items, context.position);
  if (clicked === "settle") context.onSettle(context.threadRef);
  else if (clicked === "pin") context.onPin(context.threadRef);
  else if (clicked === "rename") context.onStartRename(context.threadRef, context.threadTitle);
  else if (clicked === "mark-unread") context.onMarkUnread(scopedThreadKey(context.threadRef));
  else if (clicked === "archive") context.onArchive(context.threadRef);
  else if (clicked === "delete") context.onDelete(context.threadRef);
  else if (clicked?.startsWith("snooze:")) {
    context.onSnooze(context.threadRef, { snoozedUntil: clicked.slice("snooze:".length) });
  }
}

/** Expose scoped row actions for local pin/snooze state, server lifecycle changes, renaming, and menus. */
// fallow-ignore-next-line complexity -- v2 orchestration hook composing focused sub-hooks (see sidebarV2* modules)
export function useSidebarV2RowActions(input: {
  readonly timestampFormat: TimestampFormat;
  readonly pinOrderKeyById: Readonly<Record<string, string>>;
  readonly markThreadVisited: (threadId: string, visitedAt?: string) => void;
  readonly markThreadUnread: (
    threadId: string,
    latestTurnCompletedAt: string | null | undefined,
  ) => void;
  readonly setThreadPinned: (threadId: string, pinned: boolean) => void;
  readonly setThreadPinOrderKey: (threadId: string, orderKey: string | null) => void;
  readonly setThreadActiveOrderKey: (threadId: string, orderKey: string | null) => void;
  readonly setThreadSnooze: (
    threadId: string,
    snooze: { readonly snoozedAt: string; readonly snoozedUntil: string } | null,
  ) => void;
  readonly settleThread: (target: ScopedThreadRef) => unknown;
  readonly unsettleThread: (target: ScopedThreadRef) => unknown;
  readonly archiveThread: (target: ScopedThreadRef, opts: Record<string, never>) => unknown;
  readonly deleteThread: (target: ScopedThreadRef, opts: Record<string, never>) => unknown;
  readonly navigateToThread: (threadRef: ScopedThreadRef) => void;
  readonly orderedThreadKeysRef: Readonly<{ current: readonly string[] }>;
  readonly threadByKeyRef: Readonly<{
    current: ReadonlyMap<
      string,
      {
        readonly title: string;
        readonly latestTurn: { readonly completedAt?: string | null } | null;
      }
    >;
  }>;
}) {
  const setThreadPinned = useUiStateStore((s) => s.setThreadPinned);
  const setThreadPinOrderKey = useUiStateStore((s) => s.setThreadPinOrderKey);
  const setThreadActiveOrderKey = useUiStateStore((s) => s.setThreadActiveOrderKey);
  const setThreadSnooze = useUiStateStore((s) => s.setThreadSnooze);
  const markThreadUnread = useUiStateStore((s) => s.markThreadUnread);
  const { threadByKeyRef } = input;

  const pinThread = useCallback(
    (threadRef: ScopedThreadRef) => {
      const threadKey = scopedThreadKey(threadRef);
      // Fresh pins take the top of the arranged run: a key before the
      // smallest across ALL pinned keys, including snoozed pins hidden from
      // the list, so a new pin cannot land beneath a hidden head.
      const existingKeys = Object.values(input.pinOrderKeyById);
      const smallest =
        existingKeys.length > 0 ? existingKeys.reduce((a, b) => (a < b ? a : b)) : null;
      const key = pinOrderKeyBetween(null, smallest);
      setThreadPinned(threadKey, true);
      if (key !== null) setThreadPinOrderKey(threadKey, key);
    },
    [input.pinOrderKeyById, setThreadPinOrderKey, setThreadPinned],
  );
  const unpinThread = useCallback(
    (threadRef: ScopedThreadRef) => {
      setThreadPinned(scopedThreadKey(threadRef), false);
    },
    [setThreadPinned],
  );
  const snoozeThread = useCallback(
    (threadRef: ScopedThreadRef, preset: { readonly snoozedUntil: string }) => {
      setThreadSnooze(scopedThreadKey(threadRef), {
        snoozedAt: new Date().toISOString(),
        snoozedUntil: preset.snoozedUntil,
      });
    },
    [setThreadSnooze],
  );
  const unsnoozeThread = useCallback(
    (threadRef: ScopedThreadRef) => {
      setThreadSnooze(scopedThreadKey(threadRef), null);
    },
    [setThreadSnooze],
  );
  // Settling clears the overlay's pin and snooze the way upstream's server
  // does; the settle itself stays on forge's server path.
  const attemptSettle = useCallback(
    async (threadRef: ScopedThreadRef) => {
      try {
        await input.settleThread(threadRef);
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Failed to settle thread",
          description: error instanceof Error ? error.message : "An error occurred.",
        });
        return;
      }
      const threadKey = scopedThreadKey(threadRef);
      setThreadPinned(threadKey, false);
      setThreadPinOrderKey(threadKey, null);
      setThreadActiveOrderKey(threadKey, null);
      setThreadSnooze(threadKey, null);
    },
    [input, setThreadActiveOrderKey, setThreadPinOrderKey, setThreadPinned, setThreadSnooze],
  );
  const attemptUnsettle = useCallback(
    (threadRef: ScopedThreadRef) => {
      void input.unsettleThread(threadRef);
    },
    [input],
  );
  const [renamingThreadKey, setRenamingThreadKey] = useState<string | null>(null);
  const [renamingTitle, setRenamingTitle] = useState("");
  const startThreadRename = useCallback((threadRef: ScopedThreadRef, title: string) => {
    setRenamingThreadKey(scopedThreadKey(threadRef));
    setRenamingTitle(title);
  }, []);
  const cancelThreadRename = useCallback(() => setRenamingThreadKey(null), []);
  const commitRename = useCallback(
    (threadRef: ScopedThreadRef, newTitle: string, originalTitle: string) => {
      void commitThreadRename({
        threadRef,
        newTitle,
        originalTitle,
        finishRename: () =>
          setRenamingThreadKey((current) =>
            current === scopedThreadKey(threadRef) ? null : current,
          ),
      });
    },
    [],
  );
  const acknowledgeWoke = useCallback(
    (threadRef: ScopedThreadRef, visitedAt: string) => {
      input.markThreadVisited(scopedThreadKey(threadRef), visitedAt);
    },
    [input],
  );

  const handleThreadClick = useCallback(
    (event: React.MouseEvent, threadRef: ScopedThreadRef) => {
      if (isSidebarNestedLinkClick(event.target)) return;
      const isMac = isMacPlatform(navigator.platform);
      const isModClick = isMac ? event.metaKey : event.ctrlKey;
      const threadKey = scopedThreadKey(threadRef);
      if (isModClick) {
        event.preventDefault();
        useThreadSelectionStore.getState().toggleThread(threadKey);
        return;
      }
      if (event.shiftKey) {
        event.preventDefault();
        useThreadSelectionStore
          .getState()
          .rangeSelectTo(threadKey, input.orderedThreadKeysRef.current);
        return;
      }
      if (isTrailingDoubleClick(event.detail)) return;
      input.navigateToThread(threadRef);
    },
    [input],
  );

  const openPullRequestExternal = useCallback(
    (event: React.MouseEvent<HTMLElement>, url: string) => {
      event.preventDefault();
      event.stopPropagation();
      const api = readLocalApi();
      if (!api) {
        toastManager.add({ type: "error", title: "Link opening is unavailable." });
        return;
      }
      void api.shell.openExternal(url).catch((error: unknown) => {
        toastManager.add({
          type: "error",
          title: "Unable to open PR link",
          description: error instanceof Error ? error.message : "An error occurred.",
        });
      });
    },
    [],
  );

  const attemptArchive = useCallback(
    (threadRef: ScopedThreadRef) => {
      void input.archiveThread(threadRef, {});
    },
    [input],
  );
  const attemptDelete = useCallback(
    (threadRef: ScopedThreadRef) => {
      void input.deleteThread(threadRef, {});
    },
    [input],
  );

  const handleThreadContextMenu = useCallback(
    (threadRef: ScopedThreadRef, position: { x: number; y: number }) => {
      const threadKey = scopedThreadKey(threadRef);
      // Read at menu-open time through the render-synced ref: the title
      // seeds the rename field and the latest turn's completion stamps the
      // unread marker, both of a thread that may have changed since mount.
      const thread = threadByKeyRef.current?.get(threadKey);
      void dispatchRowContextMenu({
        threadRef,
        threadTitle: thread?.title ?? "",
        position,
        timestampFormat: input.timestampFormat,
        onSettle: attemptSettle,
        onUnsettle: attemptUnsettle,
        onPin: pinThread,
        onSnooze: snoozeThread,
        onStartRename: startThreadRename,
        onArchive: attemptArchive,
        onDelete: attemptDelete,
        onMarkUnread: (threadId: string) =>
          markThreadUnread(threadId, thread?.latestTurn?.completedAt ?? null),
      });
    },
    [
      attemptArchive,
      attemptDelete,
      attemptSettle,
      attemptUnsettle,
      input.timestampFormat,
      markThreadUnread,
      pinThread,
      snoozeThread,
      startThreadRename,
      threadByKeyRef,
    ],
  );

  return {
    pinThread,
    unpinThread,
    snoozeThread,
    unsnoozeThread,
    attemptSettle,
    attemptUnsettle,
    renamingThreadKey,
    renamingTitle,
    setRenamingTitle,
    startThreadRename,
    cancelThreadRename,
    commitRename,
    acknowledgeWoke,
    handleThreadClick,
    openPullRequestExternal,
    attemptArchive,
    attemptDelete,
    handleThreadContextMenu,
    markThreadUnread,
    threadByKeyRef,
    dragSectionGuard: undefined as unknown as SidebarSection | undefined,
  };
}
